// POWER UPS (the Array Workshop, persistent meta progression). Owner 2026-11-02: "add some deeper power-ups to the shop".
// Pure data + rules (no DOM, no three): the node table, its four branches, prerequisites, the shard cost curve and the
// effect text the shop shows. The Sim reads ranks through SimOptions.meta (sim.ts `m(id)`); the wallet / buy / refund
// live in src/app/save.ts; the shop screen is src/app/powerUpsMenu.ts. Numbers and the balance reasoning:
// Docs/DESIGN/POWER_UPS.md.
//
// Save compatibility: ranks are stored as profile.workshop[id] = rank. New nodes are simply new optional keys (absent = 0),
// so every older save loads unchanged and keeps its ranks; nothing here needs a save-version bump.

export type WorkshopId =
  // the original twelve (2026-11-01)
  | 'core' | 'lattice' | 'mesh' | 'gears' | 'ward' | 'repair' | 'pull' | 'memory' | 'salvage' | 'relay' | 'null' | 'spark'
  // deeper Power Ups (2026-11-02)
  | 'edge' | 'bloom' | 'coil' | 'prism' | 'plate' | 'ember' | 'luck' | 'opener' | 'primer' | 'exile';

export type PowerBranch = 'offense' | 'defense' | 'utility' | 'unlocks';
export const BRANCHES: readonly PowerBranch[] = ['offense', 'defense', 'utility', 'unlocks'];
export const BRANCH_LABEL: Record<PowerBranch, string> = { offense: 'OFFENSE', defense: 'DEFENSE', utility: 'UTILITY', unlocks: 'UNLOCKS' };

/** a prerequisite: node `id` at rank `rank` or higher */
export interface PowerReq { id: WorkshopId; rank: number }
export interface WorkshopTrack {
  id: WorkshopId; name: string;
  /** per-rank description (tile text) */
  desc: string;
  max: number;
  /** base of the cost curve: rank n costs cost × 1.5 × n² (rounded to 5), unless `costs` lists them */
  cost: number;
  /** explicit price per rank (unlocks and single-rank capstones) */
  costs?: readonly number[];
  branch: PowerBranch;
  icon: string;
  /** every one must be met before the first rank can be bought (ranks already owned are never taken away) */
  req?: readonly PowerReq[];
  /** total effect at rank r (r ≥ 1), as the shop shows it */
  eff(r: number): string;
}

// numbers the Sim uses (sim.ts reads them through these constants, so the shop text and the game never disagree)
export const PU = {
  hp: 10, dmg: 0.05, speed: 0.04, cd: 0.03, ward: 0.04, regen: 0.15, pull: 0.1, xp: 0.05, salvage: 0.1,
  crit: 0.01, area: 0.04, coil: 0.05, plate: 0.5,
  /** Fortune Lens per rank: chest tier odds shift (share of the plain tier moved up), extra elite chest chance, rare field items */
  luckTier: 0.03, luckChest: 0.02, luckField: 0.1,
} as const;

const pct = (x: number) => `${Math.round(x * 100)}%`;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** Shop order inside each branch = this order. */
export const WORKSHOP: WorkshopTrack[] = [
  // ---------------- OFFENSE ----------------
  { id: 'lattice', name: 'Focused Lattice', desc: '+5% damage per rank', max: 7, cost: 40, branch: 'offense', icon: '✦', eff: (r) => `+${pct(PU.dmg * r)} damage` },
  { id: 'gears', name: 'Harmonic Gears', desc: '-3% attack cooldowns per rank', max: 5, cost: 50, branch: 'offense', icon: '⚙', eff: (r) => `-${pct(PU.cd * r)} skill cooldowns` },
  { id: 'edge', name: 'Fault Sight', desc: '+1% critical hit chance per rank', max: 5, cost: 40, branch: 'offense', icon: '✷', req: [{ id: 'lattice', rank: 2 }], eff: (r) => `+${pct(PU.crit * r)} critical hit chance` },
  { id: 'bloom', name: 'Wide Bloom', desc: '+4% skill area per rank', max: 5, cost: 40, branch: 'offense', icon: '❋', req: [{ id: 'gears', rank: 1 }], eff: (r) => `+${pct(PU.area * r)} skill area` },
  { id: 'coil', name: 'Resonant Coil', desc: '+5% Surge charge per rank', max: 5, cost: 35, branch: 'offense', icon: '✪', req: [{ id: 'gears', rank: 2 }], eff: (r) => `+${pct(PU.coil * r)} Surge charge from defeated foes` },
  { id: 'prism', name: 'Twin Prism', desc: '+1 projectile for skills that fire several', max: 1, cost: 0, costs: [3000], branch: 'offense', icon: '◈', req: [{ id: 'lattice', rank: 5 }, { id: 'bloom', rank: 3 }], eff: () => '+1 projectile for skills that fire several' },
  // ---------------- DEFENSE ----------------
  { id: 'core', name: 'Reinforced Core', desc: '+10 max health per rank', max: 7, cost: 30, branch: 'defense', icon: '♥', eff: (r) => `+${PU.hp * r} max health` },
  { id: 'ward', name: 'Ceramic Ward', desc: '-4% damage taken per rank', max: 5, cost: 40, branch: 'defense', icon: '⬢', eff: (r) => `-${pct(PU.ward * r)} damage taken` },
  { id: 'plate', name: 'Bastion Plating', desc: '-0.5 damage from every hit per rank (minimum 1)', max: 4, cost: 45, branch: 'defense', icon: '▣', req: [{ id: 'ward', rank: 2 }], eff: (r) => `-${PU.plate * r} damage from every hit` },
  { id: 'repair', name: 'Quiet Repair', desc: '+0.15 health per second per rank', max: 5, cost: 40, branch: 'defense', icon: '✚', eff: (r) => `+${(PU.regen * r).toFixed(2)} health per second` },
  { id: 'spark', name: 'Reserve Spark', desc: 'Revive once per run at half health', max: 1, cost: 200, branch: 'defense', icon: '✺', eff: () => '1 revive per run' },
  { id: 'ember', name: 'Last Ember', desc: 'One more revive per run (one extra life)', max: 1, cost: 0, costs: [2400], branch: 'defense', icon: '❂', req: [{ id: 'spark', rank: 1 }, { id: 'core', rank: 5 }], eff: () => '+1 revive per run (2 with Reserve Spark)' },
  // ---------------- UTILITY ----------------
  { id: 'mesh', name: "Runner's Mesh", desc: '+4% move speed per rank', max: 5, cost: 30, branch: 'utility', icon: '»', eff: (r) => `+${pct(PU.speed * r)} move speed` },
  { id: 'pull', name: 'Far-Field Pull', desc: '+10% pickup radius per rank', max: 3, cost: 20, branch: 'utility', icon: '◎', eff: (r) => `+${pct(PU.pull * r)} pickup radius` },
  { id: 'memory', name: 'Memory Conduit', desc: '+5% experience per rank', max: 5, cost: 40, branch: 'utility', icon: '✧', eff: (r) => `+${pct(PU.xp * r)} experience` },
  { id: 'salvage', name: 'Salvage Register', desc: '+10% Fracture Shards per rank', max: 5, cost: 30, branch: 'utility', icon: '◆', eff: (r) => `+${pct(PU.salvage * r)} Fracture Shards` },
  { id: 'luck', name: 'Fortune Lens', desc: 'Better chests and rarer field items per rank', max: 5, cost: 45, branch: 'utility', icon: '☘', req: [{ id: 'salvage', rank: 1 }],
    eff: (r) => `+${pct(PU.luckTier * r)} chance of a bigger chest · +${pct(PU.luckChest * r)} elite chest drops · +${pct(PU.luckField * r)} field items, rarer kinds` },
  { id: 'relay', name: 'Choice Relay', desc: '+1 draft reroll per run per rank', max: 4, cost: 60, branch: 'utility', icon: '↻', eff: (r) => `${plural(r, 'draft reroll')} per run` },
  { id: 'null', name: 'Null Register', desc: '+1 draft ban per run per rank', max: 4, cost: 50, branch: 'utility', icon: '⊘', eff: (r) => `${plural(r, 'draft ban')} per run` },
  // ---------------- UNLOCKS ----------------
  { id: 'opener', name: 'Opening Gambit', desc: 'Start a run with any skill you have discovered (pick it in Run Setup)', max: 1, cost: 0, costs: [800], branch: 'unlocks', icon: '⚑', req: [{ id: 'relay', rank: 1 }], eff: () => 'Run Setup: choose a starting skill' },
  { id: 'primer', name: 'Trait Primer', desc: 'Start a run with an extra trait you have discovered (pick it in Run Setup)', max: 1, cost: 0, costs: [1200], branch: 'unlocks', icon: '⚘', req: [{ id: 'opener', rank: 1 }], eff: () => 'Run Setup: choose a starting trait' },
  { id: 'exile', name: 'Exile Seal', desc: 'Banish a skill or trait from the draft pool before the run (one per rank)', max: 3, cost: 0, costs: [400, 900, 1600], branch: 'unlocks', icon: '✕', req: [{ id: 'null', rank: 1 }], eff: (r) => `Run Setup: banish ${r === 1 ? '1 skill or trait' : `${r} skills or traits`} for the run` },
];

export type WorkshopRanks = Partial<Record<WorkshopId, number>>;

export const nodeOf = (id: WorkshopId): WorkshopTrack => WORKSHOP.find((t) => t.id === id)!;
export const nodesIn = (b: PowerBranch): WorkshopTrack[] => WORKSHOP.filter((t) => t.branch === b);

/** Cost of rank n (1-based) = cost × 1.5 × n² (rounded to 5), or the node's explicit price list.
 *  Owner 2026-11-01: 'too easy to max out' — was cost × n. The early ranks of the original twelve are unchanged. */
export function workshopCost(t: WorkshopTrack, nextRank: number): number {
  if (t.costs) return t.costs[Math.min(t.costs.length, Math.max(1, nextRank)) - 1];
  return Math.round((t.cost * 1.5 * nextRank * nextRank) / 5) * 5;
}
/** price of every rank of a node, and of the whole shop */
export const nodeTotal = (t: WorkshopTrack): number => { let s = 0; for (let n = 1; n <= t.max; n++) s += workshopCost(t, n); return s; };
export const shopTotal = (): number => WORKSHOP.reduce((a, t) => a + nodeTotal(t), 0);

/** prerequisites that are not met yet (empty = buyable as far as requirements go) */
export function missingReqs(ranks: WorkshopRanks, t: WorkshopTrack): PowerReq[] {
  return (t.req ?? []).filter((q) => (ranks[q.id] ?? 0) < q.rank);
}
export const reqsMet = (ranks: WorkshopRanks, t: WorkshopTrack): boolean => missingReqs(ranks, t).length === 0;

/** 'Focused Lattice 2' style labels for a requirement list */
export const reqText = (reqs: readonly PowerReq[]): string => reqs.map((q) => `${nodeOf(q.id).name} ${q.rank}`).join(', ');

/** every node at its max rank (balance tests, QA) */
export function maxRanks(): WorkshopRanks { const r: WorkshopRanks = {}; for (const t of WORKSHOP) r[t.id] = t.max; return r; }

/** What the shop shows for a node at the current ranks. */
export interface NodeView {
  id: WorkshopId; name: string; icon: string; desc: string; rank: number; max: number;
  /** price of the next rank, null when maxed */
  cost: number | null;
  now: string; next: string | null;
  /** unmet prerequisites (text), '' when none */
  needs: string;
  /** all prerequisites, met or not (text), '' when none */
  requires: string;
}
export function nodeView(ranks: WorkshopRanks, t: WorkshopTrack): NodeView {
  const rank = Math.min(t.max, ranks[t.id] ?? 0);
  return {
    id: t.id, name: t.name, icon: t.icon, desc: t.desc, rank, max: t.max,
    cost: rank >= t.max ? null : workshopCost(t, rank + 1),
    now: rank > 0 ? t.eff(rank) : 'Not bought yet',
    next: rank >= t.max ? null : t.eff(rank + 1),
    needs: reqText(missingReqs(ranks, t)),
    requires: reqText(t.req ?? []),
  };
}

// ---------------- Fortune Lens (luck) maths, shared by the loot code and the tests ----------------
/** chest tier odds after `luck` ranks: a share of the plain tier moves up (two thirds to tier 2, one third to tier 3) */
export function luckyOdds(base: readonly [number, number, number], luck: number): [number, number, number] {
  const move = Math.min(base[0], PU.luckTier * Math.max(0, luck));
  return [base[0] - move, base[1] + (move * 2) / 3, base[2] + move / 3];
}
